import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AvatarOrderResponse, Client } from '@api/client';
import { CALL_ORDER_STATUS_NUMBERS, CALL_ORDER_TYPE_NUMBERS } from '@call/call.constants';
import { AuthService } from '@services/auth.service';
import { PlatformService } from '@services/platform.service';
import CallKit, { Order } from '@plugins/android_callkit/callkit.plugin';
import Voip from '@plugins/voip/voip.plugin';
import { Observable, Subject, filter, firstValueFrom, take } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CallOrderUpdatesService {
  private readonly client = inject(Client);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformService = inject(PlatformService);
  private readonly ngZone = inject(NgZone);

  private readonly updates = new Subject<AvatarOrderResponse>();
  private readonly orders = new Map<number, AvatarOrderResponse>();
  private readonly pending = new Map<number, Promise<void>>();
  private session: number = 0;

  public get orderUpdated$(): Observable<AvatarOrderResponse> {
    return this.updates.asObservable();
  }

  constructor() {
    this.authService.isAuthenticated$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((isAuthenticated: boolean): void => {
      if (isAuthenticated) return;
      this.session++;
      this.orders.clear();
      this.pending.clear();
    });

    if (this.platformService.isAndroidApp || this.platformService.isIosApp) {
      this.authService.isAuthenticated$.pipe(
        filter(isAuthenticated => isAuthenticated),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe((): void => {
        void this.listenForNativeRejection();
      });
    }
  }

  public getUpdatedOrder(orderId: number): AvatarOrderResponse | undefined {
    return this.orders.get(orderId);
  }

  public updateOrder(order: AvatarOrderResponse): void {
    if (!this.authService.isAuthenticated || order.type !== CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL || order.id === undefined) return;
    if (!this.isFinal(order)) return;
    this.orders.set(order.id, order);
    this.updates.next(order);
  }

  public refreshOrder(orderId: number): Promise<void> {
    if (!this.authService.isAuthenticated || this.isFinal(this.orders.get(orderId))) return Promise.resolve();
    const pending = this.pending.get(orderId);
    if (pending) return pending;

    const request = this.loadOrder(orderId);
    this.pending.set(orderId, request);
    return request;
  }

  private async listenForNativeRejection(): Promise<void> {
    let destroyed = false;
    this.destroyRef.onDestroy((): void => { destroyed = true; });
    const onRejected = (order: Order): void => {
      void this.ngZone.run(async (): Promise<void> => {
        // An earlier request may have read the order before the native rejection committed.
        await this.pending.get(order.id);
        await this.refreshOrder(order.id);
      });
    };
    try {
      const listener = this.platformService.isAndroidApp
        ? await CallKit.addListener('orderRejected', onRejected)
        : await Voip.addListener('orderRejected', onRejected);
      if (destroyed) {
        await listener.remove();
        return;
      }
      this.destroyRef.onDestroy((): void => { void listener.remove(); });
    } catch (error) {
      console.error('Failed to register native call rejection listener', error);
    }
  }

  private async loadOrder(orderId: number): Promise<void> {
    const session = this.session;
    const previous = this.orders.get(orderId);
    try {
      const response = await firstValueFrom(this.client.avatarOrderGet(orderId));
      // A cancellation response or a new session can supersede this request.
      if (session !== this.session || this.orders.get(orderId) !== previous) return;
      if (response.data) this.updateOrder(response.data);
    } catch (error) {
      console.error('Failed to refresh call order', orderId, error);
    } finally {
      if (session === this.session) this.pending.delete(orderId);
    }
  }

  private isFinal(order: AvatarOrderResponse | undefined): boolean {
    return order?.status === CALL_ORDER_STATUS_NUMBERS.COMPLETED
      || order?.status === CALL_ORDER_STATUS_NUMBERS.CANCELED
      || order?.status === CALL_ORDER_STATUS_NUMBERS.REJECTED;
  }
}
